<template>
  <div class="home">
    <section class="hero-section">
      <div class="hero-content">
        <p class="hero-eyebrow">dashcom curated marketplace</p>
        <h1 class="hero-title">发现当季好物，把逛商城这件事变得更轻松。</h1>
        <p class="hero-description">
          以简洁、克制和流动感为核心，把选品、搜索、筛选和下单都压进更轻的界面里。
        </p>
        <div class="hero-actions">
          <button class="btn hero-primary" @click="scrollToProducts">开始浏览</button>
          <button class="btn btn-secondary hero-secondary" @click="showSearchModal = true">搜索商品</button>
        </div>
        <div class="hero-stats">
          <div class="hero-stat">
            <span class="hero-stat-value">{{ categories.length || 0 }}</span>
            <span class="hero-stat-label">精选分类</span>
          </div>
          <div class="hero-stat">
            <span class="hero-stat-value">{{ totalElements || products.length || 0 }}</span>
            <span class="hero-stat-label">在售商品</span>
          </div>
          <div class="hero-stat">
            <span class="hero-stat-value">{{ hotSearches.length || 0 }}</span>
            <span class="hero-stat-label">热门搜索</span>
          </div>
        </div>
      </div>
      <div class="hero-visual" aria-hidden="true">
        <div class="hero-orb hero-orb-one"></div>
        <div class="hero-orb hero-orb-two"></div>
        <div class="hero-orb hero-orb-three"></div>
        <div class="hero-panel hero-panel-front">
          <span class="hero-panel-label">Now browsing</span>
          <strong>Minimal picks</strong>
        </div>
        <div class="hero-panel hero-panel-back">
          <span class="hero-panel-label">Atmosphere</span>
          <strong>Fluid glass</strong>
        </div>
      </div>
    </section>
    <div v-if="loading" class="loading">加载中...</div>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- 分类导航区域 -->
    <div class="category-nav-section">
      <div class="category-nav-wrapper">
        <div class="category-nav">
          <button 
            class="category-item" 
            :class="{ active: selectedCategoryId === '' }"
            @click="selectCategory('')">
            精选
          </button>
          <button 
            v-for="category in categories" 
            :key="category.id" 
            class="category-item"
            :class="{ active: selectedCategoryId === category.id }"
            @click="selectCategory(category.id)">
            {{ category.name }}
          </button>
        </div>
        
        <!-- 视图切换、搜索和排序图标 -->
        <div class="action-icons">
          <!-- 视图切换 -->
          <div class="view-switch">
            <button 
              class="view-btn" 
              :class="{ active: viewMode === 'grid' }" 
              @click="switchViewMode('grid')"
              title="网格视图"
            >
              <span class="view-icon">▦</span>
            </button>
            <button 
              class="view-btn" 
              :class="{ active: viewMode === 'list' }" 
              @click="switchViewMode('list')"
              title="列表视图"
            >
              <span class="view-icon">☰</span>
            </button>
          </div>
          <button class="icon-btn" @click="showSearchModal = true" title="搜索">
            <img 
              src="/images/search.png" 
              alt="搜索" 
              class="icon-img"
              @error="handleIconError($event, 'search')"
            />
          </button>
          <button class="icon-btn" @click="showSortModal = true" title="排序">
            <img 
              src="/images/sort.png" 
              alt="排序" 
              class="icon-img"
              @error="handleIconError($event, 'sort')"
            />
          </button>
          <button class="icon-btn filter-btn" @click="showFilterModal = true" title="高级筛选">
            <span class="filter-icon">⚙</span>
            <span v-if="hasActiveFilters" class="filter-badge"></span>
          </button>
        </div>
      </div>
      
      <!-- 二级分类导航 - 只在选择了一级分类时显示 -->
      <div class="sub-category-nav" v-if="selectedCategoryId && subCategories.length > 0">
        <button 
          class="sub-category-item" 
          :class="{ active: selectedSubCategoryId === '' }"
          @click="selectSubCategory('')">
          全部
        </button>
        <button 
          v-for="subCategory in subCategories" 
          :key="subCategory.id" 
          class="sub-category-item"
          :class="{ active: selectedSubCategoryId === subCategory.id }"
          @click="selectSubCategory(subCategory.id)">
          {{ subCategory.name }}
        </button>
      </div>
    </div>

    <!-- 搜索弹窗 -->
    <div v-if="showSearchModal" class="search-modal-overlay" @click.self="showSearchModal = false">
      <div class="search-modal">
        <div class="search-form">
          <input 
            type="text" 
            v-model="searchKeyword" 
            placeholder="请输入商品名称或关键字"
            class="search-input"
            @keyup.enter="performSearchAndClose"
            @input="handleSearchInput"
            autofocus
          />
          <button @click="performSearchAndClose" class="search-btn">搜索</button>
        </div>
        
        <!-- 搜索建议/历史/热门 -->
        <div class="search-suggestions" v-if="searchKeyword || searchHistory.length > 0 || hotSearches.length > 0">
          <!-- 自动补全建议 -->
          <div v-if="searchKeyword && suggestions.length > 0" class="suggestion-section">
            <div class="section-header">
              <span class="section-icon">🔍</span>
              <span class="section-title">自动补全</span>
            </div>
            <div 
              v-for="(suggestion, index) in suggestions" 
              :key="'suggest-' + index"
              class="suggestion-item"
              @click="selectSuggestion(suggestion)"
            >
              <span class="suggestion-icon">📝</span>
              <span class="suggestion-text">{{ suggestion }}</span>
            </div>
          </div>
          
          <!-- 搜索历史 -->
          <div v-if="!searchKeyword && searchHistory.length > 0" class="suggestion-section">
            <div class="section-header">
              <span class="section-icon">📜</span>
              <span class="section-title">搜索历史</span>
              <button class="clear-history-btn" @click="clearSearchHistory">清空</button>
            </div>
            <div 
              v-for="(history, index) in searchHistory" 
              :key="'history-' + index"
              class="suggestion-item"
              @click="selectSuggestion(history)"
            >
              <span class="suggestion-icon">🔄</span>
              <span class="suggestion-text">{{ history }}</span>
              <button class="delete-history-btn" @click.stop="deleteHistory(index)">×</button>
            </div>
          </div>
          
          <!-- 热门搜索 -->
          <div v-if="!searchKeyword && hotSearches.length > 0" class="suggestion-section">
            <div class="section-header">
              <span class="section-icon">🔥</span>
              <span class="section-title">热门搜索</span>
            </div>
            <div class="hot-search-list">
              <button 
                v-for="(hot, index) in hotSearches" 
                :key="'hot-' + index"
                class="hot-search-item"
                :class="{ 'top-three': index < 3 }"
                @click="selectSuggestion(hot.keyword)"
              >
                <span class="hot-rank">{{ index + 1 }}</span>
                <span class="hot-text">{{ hot.keyword }}</span>
                <span v-if="hot.count" class="hot-count">{{ hot.count }}次</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 排序弹窗 -->
    <div v-if="showSortModal" class="sort-modal-overlay" @click.self="showSortModal = false">
      <div class="sort-modal">
        <button class="close-btn search-close-btn" @click="showSortModal = false">×</button>
        <div class="sort-options">
          <button 
            v-for="option in sortOptions" 
            :key="option.value"
            class="sort-option"
            :class="{ active: sortBy === option.value }"
            @click="toggleSort(option.value)"
          >
            {{ option.label }}
            <span v-if="sortBy === option.value" class="sort-direction">
              {{ sortDirection === 'desc' ? '↓' : '↑' }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- 高级筛选弹窗 -->
    <div v-if="showFilterModal" class="filter-modal-overlay" @click.self="showFilterModal = false">
      <div class="filter-modal">
        <div class="filter-modal-header">
          <h3>高级筛选</h3>
          <button class="close-btn" @click="showFilterModal = false">×</button>
        </div>
        <div class="filter-content">
          <!-- 价格区间 -->
          <div class="filter-section">
            <h4>价格区间</h4>
            <div class="price-range-options">
              <button 
                v-for="(range, index) in priceRanges" 
                :key="index"
                class="price-range-btn"
                :class="{ active: selectedPriceRange === index }"
                @click="selectPriceRange(index)"
              >
                {{ range.label }}
              </button>
            </div>
          </div>
          
          <!-- 其他筛选条件 -->
          <div class="filter-section">
            <h4>其他条件</h4>
            <div class="filter-checkboxes">
              <label class="filter-checkbox">
                <input type="checkbox" v-model="filterOptions.hasStock" />
                <span>仅显示有货</span>
              </label>
              <label class="filter-checkbox">
                <input type="checkbox" v-model="filterOptions.hasDiscount" />
                <span>仅显示优惠商品</span>
              </label>
            </div>
          </div>
        </div>
        <div class="filter-modal-footer">
          <button class="btn btn-secondary" @click="resetFilters">重置</button>
          <button class="btn btn-primary" @click="applyFilters">确定</button>
        </div>
      </div>
    </div>

    <!-- 商品列表区域 -->
    <div class="products-container" v-if="!loading" ref="productsSection">
      <div class="product-list" :class="{ 'list-view': viewMode === 'list' }" v-if="products.length > 0">
        <div 
          class="product-card" 
          v-for="product in products" 
          :key="product.id"
          :class="{ 'highlighted': lastViewedProductId === product.id }"
          @mouseenter="hoveredProductId = product.id"
          @mouseleave="hoveredProductId = null"
        >
          <router-link 
            :to="{ name: 'productDetail', params: { id: product.id } }" 
            class="product-image"
            @click="saveScrollPosition(product.id)"
          >
            <img
              :src="getProductImageUrl(product)"
              :alt="product.name || '商品图片'"
              class="product-img"
              @error="handleImageError($event, product.id)"
            />
          </router-link>
          
          <div class="product-info">
            <h3 class="product-name">{{ product.name }}</h3>
            <div class="product-price">¥{{ product.price.toFixed(2) }}</div>
          </div>
          
          <div class="product-actions" :class="{ 'show': hoveredProductId === product.id }">
            <button 
              class="action-btn buy-btn" 
              @click="handleBuyClick(product)"
              :disabled="!canBuy || product.stock <= 0"
            >
              购买
            </button>
            <button 
              class="action-btn cart-btn" 
              @click="addToCart(product)"
              :disabled="!canBuy || product.stock <= 0"
            >
              加入购物车
            </button>
          </div>
        </div>
      </div>

      <div v-else class="alert alert-info">
        {{ searchKeyword ? '未找到匹配的商品' : '当前没有可购买的商品。' }}
      </div>

      <div class="pagination" v-if="totalPages > 1">
        <button 
          @click="changePage(currentPage - 1)" 
          :disabled="currentPage === 0"
          class="btn btn-secondary"
        >
          上一页
        </button>
        
        <span class="page-info">
          第 {{ currentPage + 1 }} 页，共 {{ totalPages }} 页 (总计 {{ totalElements }} 条记录)
        </span>
        
        <button 
          @click="changePage(currentPage + 1)" 
          :disabled="currentPage >= totalPages - 1"
          class="btn btn-secondary"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- 尺码选择弹窗 -->
    <div v-if="showBuyForm && currentProduct" class="modal-overlay">
      <div class="size-selector-modal card modal-content">
        <div class="modal-header">
          <h2>选择尺码</h2>
          <button class="close-btn" @click="closeBuyForm">×</button>
        </div>
        
        <div v-if="loadingSizeDetails" class="loading">加载尺码信息中...</div>
        <div v-else-if="sizeDetails.length > 0">
          <div class="size-options">
            <div 
              v-for="sizeDetail in sizeDetails" 
              :key="sizeDetail.id"
              class="size-option"
              :class="{ 
                'selected': selectedSizeDetail?.id === sizeDetail.id,
                'out-of-stock': sizeDetail.stock <= 0
              }"
              @click="selectSize(sizeDetail)"
            >
              <span class="size-code">{{ sizeDetail.size.code }}</span>
              <span class="size-name">{{ sizeDetail.size.name }}</span>
              <span class="size-stock">
                {{ sizeDetail.stock > 0 ? `库存: ${sizeDetail.stock}` : '无货' }}
              </span>
              <span v-if="sizeDetail.extraPrice > 0" class="size-extra-price">
                +¥{{ sizeDetail.extraPrice.toFixed(2) }}
              </span>
            </div>
          </div>
          
          <div v-if="selectedSizeDetail" class="selected-size-info">
            <p>已选择: {{ selectedSizeDetail.size.code }} - {{ selectedSizeDetail.size.name }}</p>
            <p v-if="selectedSizeDetail.extraPrice > 0">
              附加价格: ¥{{ selectedSizeDetail.extraPrice.toFixed(2) }}
            </p>
          </div>
          
          <div class="quantity-selector">
            <label>购买数量:</label>
            <div class="quantity-controls">
              <button class="quantity-btn" @click="decreaseQuantity" :disabled="quantity <= 1">-</button>
              <input type="number" v-model.number="quantity" min="1" :max="selectedSizeDetail?.stock || currentProduct.stock">
              <button class="quantity-btn" @click="increaseQuantity" :disabled="quantity >= (selectedSizeDetail?.stock || currentProduct.stock)">+</button>
            </div>
          </div>
          
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBuyForm">取消</button>
            <button 
              class="btn btn-primary" 
              @click="confirmSizeAndBuy"
              :disabled="!selectedSizeDetail || selectedSizeDetail.stock <= 0"
            >
              确认购买
            </button>
          </div>
        </div>
        <div v-else>
          <p class="no-size-info">该商品无需选择尺码</p>
          <div class="quantity-selector">
            <label>购买数量:</label>
            <div class="quantity-controls">
              <button class="quantity-btn" @click="decreaseQuantity" :disabled="quantity <= 1">-</button>
              <input type="number" v-model.number="quantity" min="1" :max="currentProduct.stock">
              <button class="quantity-btn" @click="increaseQuantity" :disabled="quantity >= currentProduct.stock">+</button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBuyForm">取消</button>
            <button class="btn btn-primary" @click="confirmSizeAndBuy">确认购买</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 购买表单弹窗 - 使用统一CheckoutForm组件 -->
    <div v-if="showCheckoutForm" class="modal-overlay" @click.self="closeCheckoutForm">
      <div class="checkout-modal unified-checkout">
        <div class="modal-header">
          <h2>确认订单</h2>
          <button class="close-btn" @click="closeCheckoutForm">×</button>
        </div>
        <CheckoutForm 
          :items="checkoutItems" 
          @close="closeCheckoutForm"
          @order-created="onOrderCreated"
          @payment-success="onPaymentSuccess"
        />
      </div>
    </div>
    
    <!-- 地址选择器弹窗 -->
    <div v-if="showAddressSelector" class="modal-overlay">
      <div class="address-selector card modal-content">
        <h3>选择收货地址</h3>
        <div v-if="loadingAddresses" class="loading">加载中...</div>
        <div v-else-if="addresses.length === 0" class="no-address">
          <p>暂无收货地址</p>
          <router-link to="/addresses" class="btn" @click="showAddressSelector = false; showBuyForm = false">去添加地址</router-link>
        </div>
        <div v-else class="address-list">
          <div 
            v-for="address in addresses" 
            :key="address.id" 
            class="address-option"
            :class="{ 'selected': tempSelectedAddressId === address.id }"
            @click="tempSelectedAddressId = address.id"
          >
            <div class="address-info">
              <span class="receiver-name">{{ address.receiverName }}</span>
              <span class="phone">{{ address.phone }}</span>
              <span v-if="address.isDefault" class="default-badge">默认</span>
            </div>
            <div class="address-detail">
              {{ address.province }} {{ address.city }} {{ address.district }} {{ address.detailAddress }}
            </div>
          </div>
        </div>
        <div class="selector-actions">
          <button class="btn" @click="showAddressSelector = false">取消</button>
          <button class="btn btn-primary" @click="confirmAddressSelection" :disabled="!tempSelectedAddressId">确认</button>
        </div>
      </div>
    </div>

    <!-- 支付成功弹窗 -->
    <div v-if="buySuccess" class="modal-overlay">
      <div class="success-modal card modal-content">
        <div class="success-icon">✓</div>
        <h3>支付成功</h3>
        <p>订单 #{{ createdOrderId }} 支付成功</p>
        <div class="success-actions">
          <button class="btn" @click="viewOrderDetail">查看订单详情</button>
          <button class="btn btn-primary" @click="continueShopping">继续购物</button>
        </div>
      </div>
    </div>

    <!-- 统一非模态Toast提示 -->
    <div class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        class="toast"
        :class="toast.type"
      >
        <span class="toast-icon">{{ getToastIcon(toast.type) }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <button class="toast-close" @click="removeToast(toast.id)">×</button>
      </div>
    </div>

    <!-- AI 导购组件 -->
    <AIChat />
  </div>
</template>

<script>
import axios from 'axios';
import AIChat from '../components/AIChat.vue';
import CheckoutForm from '@/components/CheckoutForm.vue';

export default {
  name: 'HomeView',
  components: { AIChat, CheckoutForm },
  data() {
    return {
      products: [],
      categories: [],
      subCategories: [],
      selectedCategoryId: '',
      selectedSubCategoryId: '',
      currentProduct: null,
      loading: true,
      error: null,
      showBuyForm: false,
      showCheckoutForm: false,
      buyer: { notes: '' },
      quantity: 1,
      // 尺码相关
      sizeDetails: [],
      selectedSizeDetail: null,
      loadingSizeDetails: false,
      buySuccess: false,
      // Toast提示相关
      toasts: [],
      submitting: false,
      searchKeyword: '',
      sortBy: 'createdAt',
      sortDirection: 'desc',
      currentPage: 0,
      pageSize: 20,
      totalPages: 0,
      totalElements: 0,
      addresses: [],
      selectedAddress: null,
      showAddressSelector: false,
      loadingAddresses: false,
      tempSelectedAddressId: null,
      hoveredProductId: null,
      viewMode: 'grid', // grid: 网格视图, list: 列表视图
      // 维持用户任务上下文
      lastViewedProductId: null, // 最近查看的商品ID
      // 搜索相关
      searchHistory: [], // 搜索历史
      suggestions: [], // 自动补全建议
      hotSearches: [ // 热门搜索
        { keyword: '无线耳机', count: 1280 },
        { keyword: '无线耳机 降噪', count: 856 },
        { keyword: '无线耳机 运动', count: 543 },
        { keyword: 'iPhone 15 手机壳', count: 432 },
        { keyword: '机械键盘', count: 321 },
        { keyword: 'Switch 2', count: 289 }
      ],
      showSearchModal: false,
      showSortModal: false,
      sortOptions: [
        { value: 'createdAt', label: '上架时间' },
        { value: 'price', label: '价格' },
        { value: 'salesCount', label: '销量' }
      ],
      // 高级筛选相关
      showFilterModal: false,
      filterOptions: {
        priceRange: {
          min: null,
          max: null
        },
        hasStock: false,
        hasDiscount: false
      },
      priceRanges: [
        { label: '全部价格', min: null, max: null },
        { label: '¥0 - ¥100', min: 0, max: 100 },
        { label: '¥100 - ¥500', min: 100, max: 500 },
        { label: '¥500 - ¥1000', min: 500, max: 1000 },
        { label: '¥1000 - ¥5000', min: 1000, max: 5000 },
        { label: '¥5000以上', min: 5000, max: null }
      ],
      selectedPriceRange: null,
      createdOrderId: null
    };
  },
  computed: {
    isBuyerLoggedIn() {
      return !!localStorage.getItem('customerToken');
    },
    isSellerLoggedIn() {
      return !!localStorage.getItem('sellerToken');
    },
    canBuy() {
      return this.isBuyerLoggedIn && !this.isSellerLoggedIn;
    },
    hasActiveFilters() {
      return this.selectedPriceRange !== null || 
             this.filterOptions.hasStock || 
             this.filterOptions.hasDiscount;
    },
    // 购买商品列表（用于CheckoutForm）
    checkoutItems() {
      if (!this.currentProduct) return [];
      return [{
        id: this.currentProduct.id,
        productId: this.currentProduct.id,
        productName: this.currentProduct.name,
        price: this.currentProduct.price,
        quantity: this.quantity,
        imageUrl: this.getProductImageUrl(this.currentProduct),
        sizeId: this.selectedSizeDetail?.id || null,
        sizeName: this.selectedSizeDetail?.size?.name || null,
        sizeExtraPrice: this.selectedSizeDetail?.extraPrice || 0
      }];
    }
  },
  created() {
    this.fetchCategories();
    this.fetchProducts().then(() => {
      // 商品加载完成后恢复滚动位置
      this.restoreScrollPosition();
    });
    this.loadSearchHistory();
  },
  watch: {
    selectedCategoryId(newVal) {
      this.selectedSubCategoryId = '';
      this.loadSubCategories(newVal);
      this.currentPage = 0;
      this.fetchProducts();
    },
    selectedSubCategoryId() {
      this.currentPage = 0;
      this.fetchProducts();
    },
    sortBy() {
      this.currentPage = 0;
      this.fetchProducts();
    },
    sortDirection() {
      this.currentPage = 0;
      this.fetchProducts();
    }
  },
  methods: {
    scrollToProducts() {
      const section = this.$refs.productsSection
      if (!section) return
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    // 保存滚动位置和商品ID（维持用户任务上下文）
    saveScrollPosition(productId) {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      sessionStorage.setItem('homeScrollPosition', scrollPosition.toString());
      sessionStorage.setItem('lastViewedProductId', productId.toString());
      sessionStorage.setItem('homeViewMode', this.viewMode);
    },
    
    // 恢复滚动位置和高亮商品
    restoreScrollPosition() {
      const savedPosition = sessionStorage.getItem('homeScrollPosition');
      const savedProductId = sessionStorage.getItem('lastViewedProductId');
      const savedViewMode = sessionStorage.getItem('homeViewMode');
      
      if (savedProductId) {
        this.lastViewedProductId = parseInt(savedProductId);
        // 不清除高亮，保持到用户下次点击其他商品
      }
      
      if (savedViewMode) {
        this.viewMode = savedViewMode;
        sessionStorage.removeItem('homeViewMode');
      }
      
      if (savedPosition) {
        this.$nextTick(() => {
          window.scrollTo(0, parseInt(savedPosition));
          sessionStorage.removeItem('homeScrollPosition');
        });
      }
    },
    
    // 切换视图模式
    switchViewMode(mode) {
      this.viewMode = mode;
      // 切换视图时清除高亮
      this.lastViewedProductId = null;
      sessionStorage.removeItem('lastViewedProductId');
    },
    
    // 选择价格区间
    selectPriceRange(index) {
      this.selectedPriceRange = this.selectedPriceRange === index ? null : index;
      if (this.selectedPriceRange !== null) {
        const range = this.priceRanges[index];
        this.filterOptions.priceRange.min = range.min;
        this.filterOptions.priceRange.max = range.max;
      } else {
        this.filterOptions.priceRange.min = null;
        this.filterOptions.priceRange.max = null;
      }
    },
    
    // 应用筛选
    applyFilters() {
      this.currentPage = 0;
      this.fetchProducts();
      this.showFilterModal = false;
    },
    
    // 重置筛选
    resetFilters() {
      this.selectedPriceRange = null;
      this.filterOptions = {
        priceRange: { min: null, max: null },
        hasStock: false,
        hasDiscount: false
      };
      this.currentPage = 0;
      this.fetchProducts();
    },
    
    selectCategory(categoryId) {
      this.selectedCategoryId = categoryId;
    },
    selectSubCategory(subCategoryId) {
      this.selectedSubCategoryId = subCategoryId;
    },
    async fetchCategories() {
      try {
        const response = await axios.get('/categories/active');
        this.categories = response.data.data || [];
      } catch (error) {
        console.error('获取分类信息失败:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadSubCategories(categoryId) {
      try {
        if (categoryId) {
          const response = await axios.get(`/sub-categories/category/${categoryId}`);
          this.subCategories = Array.isArray(response.data) ? response.data : [];
        } else {
          this.subCategories = [];
        }
      } catch (error) {
        console.error('获取二级分类信息失败:', error);
        this.subCategories = [];
      }
    },
    async fetchProducts() {
      try {
        this.loading = true;
        
        let url;
        const hasSearchKeyword = this.searchKeyword && this.searchKeyword.trim();
        
        if (hasSearchKeyword) {
          if (this.selectedSubCategoryId) {
            url = `/products/sub-category/${this.selectedSubCategoryId}/search`;
          } else if (this.selectedCategoryId) {
            url = `/products/category/${this.selectedCategoryId}/search`;
          } else {
            url = '/products/search';
          }
          url += `?keyword=${encodeURIComponent(this.searchKeyword.trim())}&sort=${this.sortBy},${this.sortDirection}`;
        } else {
          if (this.selectedSubCategoryId) {
            url = `/products/sub-category/${this.selectedSubCategoryId}/active`;
          } else if (this.selectedCategoryId) {
            url = `/products/category/${this.selectedCategoryId}/active`;
          } else {
            url = '/products/active-list';
          }
          url += `?sort=${this.sortBy},${this.sortDirection}`;
        }
        
        const response = await axios.get(url);
        const data = response.data.data || response.data;
        let productsArray = Array.isArray(data) ? data : [data].filter(Boolean);
        
        if (!response.data.data) {
          productsArray.sort((a, b) => {
            let valueA, valueB;
            switch (this.sortBy) {
              case 'price':
                valueA = parseFloat(a.price) || 0;
                valueB = parseFloat(b.price) || 0;
                break;
              case 'salesCount':
                valueA = parseInt(a.salesCount) || 0;
                valueB = parseInt(b.salesCount) || 0;
                break;
              case 'createdAt':
              default:
                valueA = new Date(a.createdAt || 0);
                valueB = new Date(b.createdAt || 0);
                break;
            }
            if (this.sortDirection === 'asc') {
              return valueA > valueB ? 1 : -1;
            } else {
              return valueA < valueB ? 1 : -1;
            }
          });
        }
        
        // 应用前端筛选
        if (this.filterOptions.priceRange.min !== null || this.filterOptions.priceRange.max !== null) {
          productsArray = productsArray.filter(product => {
            const price = parseFloat(product.price) || 0;
            const min = this.filterOptions.priceRange.min;
            const max = this.filterOptions.priceRange.max;
            if (min !== null && price < min) return false;
            if (max !== null && price > max) return false;
            return true;
          });
        }
        
        if (this.filterOptions.hasStock) {
          productsArray = productsArray.filter(product => product.stock > 0);
        }
        
        if (this.filterOptions.hasDiscount) {
          productsArray = productsArray.filter(product => product.originalPrice && product.originalPrice > product.price);
        }
        
        this.products = productsArray;
        this.totalPages = 1;
        this.totalElements = productsArray.length;
        this.currentPage = 0;
      } catch (error) {
        console.error('获取商品信息失败:', error);
        this.error = '获取商品信息失败，请稍后重试。';
        this.products = [];
        this.totalPages = 0;
        this.totalElements = 0;
      } finally {
        this.loading = false;
      }
    },
    handleImageError(event, productId) {
      const defaultImage = 'https://picsum.photos/seed/product-' + productId + '/300/300';
      if (event.target.src !== defaultImage) {
        event.target.src = defaultImage;
      }
    },
    handleIconError(event, type) {
      event.target.style.display = 'none';
      const iconText = type === 'search' ? '🔍' : '↕';
      event.target.parentElement.innerHTML = iconText;
    },
    getProductImageUrl(product) {
      if (!product.imageUrl) {
        return 'https://picsum.photos/seed/product-' + product.id + '/300/300';
      }
      if (!product.imageUrl.startsWith('http')) {
        return '/api/files/images/' + product.imageUrl;
      }
      if (product.imageUrl.includes('huaban.com')) {
        return 'https://picsum.photos/seed/product-' + product.id + '/300/300';
      }
      return product.imageUrl;
    },
    async handleBuyClick(product) {
      if (!this.isBuyerLoggedIn) {
        this.error = '未登录，跳转至登录界面';
        setTimeout(() => {
          this.$router.push('/login');
        }, 2000);
      } else {
        this.currentProduct = product;
        this.quantity = 1;
        this.selectedSizeDetail = null;
        await this.fetchSizeDetails(product.id);
        this.showBuyForm = true;
      }
    },
    async fetchAddresses() {
      this.loadingAddresses = true;
      try {
        const username = localStorage.getItem('customerUsername');
        const response = await this.$axios.get('/addresses', {
          headers: { 'X-Username': username }
        });
        
        if (response.data.success) {
          this.addresses = response.data.addresses || [];
          if (this.addresses.length > 0) {
            if (this.selectedAddress) {
              const updatedAddress = this.addresses.find(a => a.id === this.selectedAddress.id);
              if (updatedAddress) {
                this.selectedAddress = updatedAddress;
                this.tempSelectedAddressId = updatedAddress.id;
              } else {
                const defaultAddr = this.addresses.find(a => a.isDefault);
                this.selectedAddress = defaultAddr || this.addresses[0];
                this.tempSelectedAddressId = this.selectedAddress.id;
              }
            } else {
              const defaultAddr = this.addresses.find(a => a.isDefault);
              this.selectedAddress = defaultAddr || this.addresses[0];
              this.tempSelectedAddressId = this.selectedAddress.id;
            }
          }
        }
      } catch (error) {
        console.error('获取地址失败:', error);
      } finally {
        this.loadingAddresses = false;
      }
    },
    confirmAddressSelection() {
      if (this.tempSelectedAddressId) {
        this.selectedAddress = this.addresses.find(a => a.id === this.tempSelectedAddressId);
        this.showAddressSelector = false;
      }
    },
    async fetchSizeDetails(productId) {
      this.loadingSizeDetails = true;
      try {
        const response = await this.$axios.get(`/products/${productId}/sizes`);
        this.sizeDetails = response.data.data || response.data || [];
      } catch (error) {
        console.error('获取尺码详情失败:', error);
        this.sizeDetails = [];
      } finally {
        this.loadingSizeDetails = false;
      }
    },

    selectSize(sizeDetail) {
      if (sizeDetail.stock > 0) {
        this.selectedSizeDetail = sizeDetail;
      }
    },

    increaseQuantity() {
      const maxStock = this.selectedSizeDetail ? this.selectedSizeDetail.stock : this.currentProduct.stock;
      if (this.quantity < maxStock) {
        this.quantity++;
      }
    },

    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },

    closeBuyForm() {
      this.showBuyForm = false;
      this.selectedSizeDetail = null;
      this.quantity = 1;
    },

    confirmSizeAndBuy() {
      if (this.sizeDetails.length > 0 && !this.selectedSizeDetail) {
        alert('请选择尺码');
        return;
      }
      this.showBuyForm = false;
      this.showCheckoutForm = true;
    },

    closeCheckoutForm() {
      this.showCheckoutForm = false;
      this.selectedSizeDetail = null;
      this.quantity = 1;
    },

    onOrderCreated(orderId) {
      this.createdOrderId = orderId;
      this.showToast('订单创建成功', 'success');
    },

    onPaymentSuccess() {
      this.showToast('支付成功', 'success');
    },
    
    viewOrderDetail() {
      this.buySuccess = false;
      this.$router.push('/orders');
    },
    
    continueShopping() {
      this.buySuccess = false;
      this.createdOrderId = null;
      this.currentProduct = null;
    },
    performSearch() {
      this.currentPage = 0;
      this.fetchProducts();
    },
    performSearchAndClose() {
      this.performSearch();
      // 保存搜索历史
      if (this.searchKeyword.trim()) {
        this.saveSearchHistory(this.searchKeyword.trim());
      }
      this.showSearchModal = false;
    },
    
    handleSearchInput() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        this.suggestions = [];
        return;
      }
      
      // 生成自动补全建议
      const allKeywords = [
        '无线耳机', '无线耳机 降噪', '无线耳机 运动', '蓝牙耳机',
        'iPhone 15', 'iPhone 15 手机壳', 'iPhone 15 钢化膜',
        '机械键盘', '机械键盘 青轴', '机械键盘 红轴',
        'Switch', 'Switch 2', 'Switch 游戏',
        '笔记本电脑', '轻薄本', '游戏本',
        '运动鞋', '跑步鞋', '篮球鞋',
        '手表', '智能手表', '运动手表'
      ];
      
      this.suggestions = allKeywords
        .filter(k => k.toLowerCase().includes(keyword.toLowerCase()))
        .slice(0, 5);
    },
    
    selectSuggestion(keyword) {
      this.searchKeyword = keyword;
      this.performSearch();
      this.saveSearchHistory(keyword);
      this.showSearchModal = false;
    },
    
    saveSearchHistory(keyword) {
      // 移除重复项
      this.searchHistory = this.searchHistory.filter(h => h !== keyword);
      // 添加到开头
      this.searchHistory.unshift(keyword);
      // 最多保留10条记录
      if (this.searchHistory.length > 10) {
        this.searchHistory = this.searchHistory.slice(0, 10);
      }
      // 保存到localStorage
      localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
    },
    
    loadSearchHistory() {
      try {
        const saved = localStorage.getItem('searchHistory');
        if (saved) {
          this.searchHistory = JSON.parse(saved);
        }
      } catch (e) {
        console.error('加载搜索历史失败:', e);
        this.searchHistory = [];
      }
    },
    
    clearSearchHistory() {
      this.searchHistory = [];
      localStorage.removeItem('searchHistory');
    },
    
    deleteHistory(index) {
      this.searchHistory.splice(index, 1);
      localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
    },
    
    toggleSort(sortValue) {
      if (this.sortBy === sortValue) {
        this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      } else {
        this.sortBy = sortValue;
        this.sortDirection = 'desc';
      }
      this.showSortModal = false;
    },
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
    },
    changePage(newPage) {
      if (newPage >= 0 && newPage < this.totalPages) {
        this.currentPage = newPage;
        this.fetchProducts();
      }
    },
    async addToCart(product) {
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        const response = await this.$axios.post('/cart/add', {
          productId: product.id,
          quantity: 1
        }, {
          headers: { 'X-Username': encodedUsername }
        });
        
        if (response.data.success) {
          this.showToast(response.data.message, 'success');
        } else {
          this.showToast('错误: ' + response.data.message, 'error');
        }
      } catch (error) {
        console.error('加入购物车失败:', error);
        this.showToast('加入购物车失败，请稍后重试。', 'error');
      }
    },
    
    showToast(message, type = 'info', duration = 3000) {
      const toast = {
        id: Date.now() + Math.random(),
        message,
        type
      };
      this.toasts.push(toast);
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(toast.id);
        }, duration);
      }
    },
    
    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id);
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    },
    
    getToastIcon(type) {
      const icons = {
        success: '✓',
        error: '✕',
        info: 'ℹ',
        warning: '!'
      };
      return icons[type] || 'ℹ';
    }
  }
};
</script>

<style scoped>
.home {
  width: 100%;
  margin: 0;
  padding: 0;
  padding-top: 75px;
  position: relative;
  z-index: 1;
}

.loading {
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #666;
}

.category-nav-section {
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
}

.category-nav-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.category-nav {
  display: flex;
  gap: 25px;
}

.category-item {
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 0;
  transition: color 0.2s;
  position: relative;
}

.category-item:hover {
  color: #333;
}

.category-item.active {
  color: #333;
  font-weight: bold;
}

.category-item.active::after {
  content: '';
  position: absolute;
  bottom: -14px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #333;
}

.sub-category-nav {
  display: flex;
  justify-content: center;
  gap: 15px;
  padding: 12px 20px;
  background-color: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.sub-category-item {
  background: none;
  border: none;
  color: #888;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.sub-category-item:hover {
  color: #333;
  background-color: #f0f0f0;
}

.sub-category-item.active {
  color: #fff;
  background-color: #E53935;
  font-weight: 500;
}

.action-icons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-icons .view-switch {
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 2px;
}

.action-icons .view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  border-radius: 3px;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  transition: all 0.2s;
}

.action-icons .view-btn:hover {
  background-color: #e0e0e0;
}

.action-icons .view-btn.active {
  background-color: #E02E24;
  color: white;
}

.action-icons .view-icon {
  font-size: 14px;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.icon-btn:hover {
  background-color: #f0f0f0;
}

.icon-img {
  width: 24px;
  height: 24px;
  display: block;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-content {
  width: 100%;
  max-width: 600px;
  margin: 20px;
  max-height: 90vh;
  overflow-y: auto;
}

.checkout-modal {
  width: 100%;
  max-width: 600px;
  margin: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.checkout-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.checkout-modal .modal-header h2 {
  margin: 0;
}

.checkout-modal .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
}

.checkout-modal .close-btn:hover {
  color: #333;
}

.checkout-modal > :not(.modal-header) {
  padding: 20px;
  overflow-y: auto;
}

.checkout-modal .address-section {
  padding: 15px 20px;
  background-color: #f8f9fa;
  margin: 15px;
  border-radius: 8px;
}

.checkout-modal .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.checkout-modal .section-header h3 {
  font-size: 16px;
  margin: 0;
}

.checkout-modal .selected-address {
  background-color: white;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.checkout-modal .address-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.checkout-modal .receiver-name {
  font-weight: bold;
}

.checkout-modal .phone {
  color: #666;
}

.checkout-modal .default-badge {
  background-color: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.checkout-modal .address-detail {
  font-size: 13px;
  color: #555;
}

.checkout-modal .no-address {
  text-align: center;
  padding: 20px;
  color: #666;
}

.checkout-modal .products-section {
  padding: 0 20px;
  margin-bottom: 15px;
}

.checkout-modal .products-section h3 {
  font-size: 16px;
  margin-bottom: 12px;
}

.checkout-modal .product-list {
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
}

.checkout-modal .product-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.checkout-modal .product-row:last-child {
  border-bottom: none;
}

.checkout-modal .product-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.checkout-modal .product-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
}

.checkout-modal .product-detail {
  display: flex;
  flex-direction: column;
}

.checkout-modal .product-detail .product-name {
  font-size: 14px;
  color: #333;
}

.checkout-modal .product-detail .product-price {
  font-size: 14px;
  color: #e74c3c;
}

.checkout-modal .product-quantity {
  font-size: 14px;
  color: #666;
}

.checkout-modal .product-total {
  font-size: 14px;
  font-weight: bold;
  color: #e74c3c;
}

.checkout-modal .amount-section {
  padding: 0 20px;
  margin-bottom: 15px;
}

.checkout-modal .amount-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.checkout-modal .amount-row.total {
  font-weight: bold;
  font-size: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.checkout-modal .notes-section {
  padding: 0 20px;
  margin-bottom: 20px;
}

.checkout-modal .notes-section label {
  display: block;
  margin-bottom: 16px !important;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.checkout-modal .notes-section textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 80px;
  resize: vertical;
  font-size: 14px;
  font-family: inherit;
}

.checkout-modal .modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.checkout-modal .btn {
  padding: 8px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.checkout-modal .btn-primary {
  background-color: #007bff;
  color: white;
}

.checkout-modal .btn-primary:hover {
  background-color: #0056b3;
}

.checkout-modal .btn-secondary {
  background-color: #6c757d;
  color: white;
}

.checkout-modal .btn-secondary:hover {
  background-color: #545b62;
}

.payment-modal {
  max-width: 450px;
}

.payment-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.payment-modal .modal-header h3 {
  margin: 0;
}

.payment-modal .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
}

.payment-modal .close-btn:hover {
  color: #333;
}

.payment-modal .modal-body {
  padding: 20px;
}

.payment-info {
  padding: 20px;
  text-align: center;
  background-color: #fff5f5;
  border-radius: 8px;
  margin-bottom: 20px;
}

.payment-info p {
  margin-bottom: 8px;
  color: #666;
}

.payment-info .total-amount {
  font-size: 24px;
  font-weight: bold;
  color: #e74c3c;
  margin-top: 15px;
}

.payment-methods {
  padding: 0;
}

.method-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.method-item:hover {
  border-color: #e74c3c;
}

.method-item input[type="radio"] {
  width: 18px;
  height: 18px;
}

.method-item label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  flex: 1;
}

.method-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.payment-modal .modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 15px;
}

.payment-modal .btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: background-color 0.2s;
}

.payment-modal .btn-secondary {
  background-color: #6c757d;
  color: white;
}

.payment-modal .btn-secondary:hover {
  background-color: #545b62;
}

.payment-modal .btn-primary {
  background-color: #e74c3c;
  color: white;
}

.payment-modal .btn-primary:hover {
  background-color: #c62828;
}

.search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.search-modal {
  background-color: white;
  padding: 15px 20px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
}

.search-form {
  display: flex;
  gap: 12px;
  max-width: 800px;
  margin: 0 auto;
  align-items: center;
}

.search-input {
  flex: 1;
  padding: 12px 20px;
  border: 2px solid #E53935;
  border-radius: 30px;
  font-size: 15px;
  outline: none;
  transition: box-shadow 0.2s;
}

.search-input:focus {
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.2);
}

.search-btn {
  background-color: #E53935;
  color: white;
  padding: 12px 30px;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  font-size: 15px;
  font-weight: bold;
  transition: background-color 0.2s;
}

.search-btn:hover {
  background-color: #C62828;
}

/* 搜索建议区域 */
.search-suggestions {
  max-width: 800px;
  margin: 15px auto 0;
  border-top: 1px solid #eee;
  padding-top: 15px;
}

.suggestion-section {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}

.section-icon {
  font-size: 16px;
}

.section-title {
  font-weight: bold;
}

.clear-history-btn {
  margin-left: auto;
  padding: 4px 10px;
  font-size: 12px;
  color: #999;
  border: none;
  background: none;
  cursor: pointer;
}

.clear-history-btn:hover {
  color: #E53935;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.suggestion-item:hover {
  background-color: #f5f5f5;
}

.suggestion-icon {
  font-size: 14px;
  width: 20px;
}

.suggestion-text {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.delete-history-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: #999;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-history-btn:hover {
  color: #E53935;
}

/* 热门搜索 */
.hot-search-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.hot-search-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background-color: #f8f9fa;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  transition: all 0.2s;
}

.hot-search-item:hover {
  background-color: #ffebee;
  color: #E53935;
}

.hot-search-item.top-three {
  background-color: #fff3e0;
  color: #E53935;
}

.hot-search-item.top-three:hover {
  background-color: #ffe0b2;
}

.hot-rank {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ddd;
  color: white;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hot-search-item.top-three .hot-rank {
  background-color: #E53935;
}

.hot-text {
  font-weight: 500;
}

.hot-count {
  font-size: 11px;
  color: #999;
}

.hot-search-item.top-three .hot-count {
  color: #E53935;
}

.search-close-btn {
  position: absolute;
  top: 12px;
  right: 20px;
  font-size: 28px;
  color: #999;
  padding: 0;
  line-height: 1;
}

.search-close-btn:hover {
  color: #333;
}

.sort-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.sort-modal {
  background-color: white;
  padding: 15px 20px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
}

.sort-options {
  display: flex;
  justify-content: center;
  gap: 15px;
  max-width: 600px;
  margin: 0 auto;
}

.sort-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: 2px solid #e1e5e9;
  border-radius: 30px;
  cursor: pointer;
  font-size: 14px;
  background-color: white;
  transition: all 0.2s;
}

.sort-option:hover {
  border-color: #E53935;
  color: #E53935;
}

.sort-option.active {
  background-color: #E53935;
  color: white;
  border-color: #E53935;
}

.sort-direction {
  margin-left: 10px;
}

.products-container {
  margin-top: 0;
  background-color: #f5f5f5;
  padding: 15px;
}

.product-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  width: 100%;
  max-width: 100%;
  width: 100%;
}

@media (max-width: 1200px) {
  .product-list {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .product-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .product-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .product-list {
    grid-template-columns: 1fr;
  }
}

/* 列表视图样式 */
.product-list.list-view {
  grid-template-columns: 1fr !important;
  gap: 10px;
}

.product-list.list-view .product-card {
  display: flex;
  align-items: center;
  padding: 15px;
  height: auto;
  position: relative;
}

.product-list.list-view .product-image {
  width: 120px;
  height: 120px;
  padding-bottom: 0;
  flex-shrink: 0;
}

.product-list.list-view .product-img {
  width: 100%;
  height: 100%;
}

.product-list.list-view .product-info {
  flex: 1;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-list.list-view .product-name {
  height: auto;
  margin-bottom: 10px;
  font-size: 16px;
}

.product-list.list-view .product-price {
  font-size: 22px;
  margin-bottom: 8px;
}

.product-list.list-view .product-actions {
  position: static;
  display: flex;
  gap: 10px;
  opacity: 1;
  transform: none;
  background: none;
  padding: 0;
}

.product-list.list-view .product-card:hover {
  transform: none;
}

.product-card {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
}

.product-card:hover {
  transform: scale(1.03);
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
}

/* 高亮最近查看的商品 */
.product-card.highlighted {
  border: 3px solid #ff4400;
  box-shadow: 0 0 20px rgba(255, 68, 0, 0.4);
  animation: highlightPulse 0.5s ease;
}

@keyframes highlightPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

.product-image {
  display: block;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  position: relative;
  overflow: hidden;
}

.product-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-img {
  transform: scale(1.05);
}

.product-info {
  padding: 12px;
}

.product-name {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  line-height: 1.4;
  height: 2.8em;
}

.product-price {
  font-size: 19px;
  font-weight: bold;
  color: #333;
  margin: 0;
}

.product-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.product-actions.show {
  transform: translateY(0);
}

.action-btn {
  flex: 1;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.buy-btn {
  background-color: rgba(229, 57, 53, 0.9);
  color: white;
}

.buy-btn:hover:not(:disabled) {
  background-color: #C62828;
}

.cart-btn {
  background-color: rgba(255, 112, 67, 0.9);
  color: white;
}

.cart-btn:hover:not(:disabled) {
  background-color: #F4511E;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 30px;
  padding: 20px;
}

.page-info {
  color: #666;
  font-size: 14px;
}

.btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.buy-form {
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  min-width: 400px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.address-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
}

.selected-address {
  background-color: white;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.address-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.receiver-name {
  font-weight: bold;
  min-width: 80px;
}

.phone {
  color: #666;
  min-width: 110px;
}

.default-badge {
  background-color: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.address-detail {
  font-size: 13px;
  color: #555;
}

.no-address {
  text-align: center;
  padding: 20px;
  color: #666;
}

.address-selector {
  width: 90%;
  max-width: 500px;
  background-color: white;
  padding: 20px;
  border-radius: 12px;
}

/* 高级筛选按钮样式 */
.filter-btn {
  position: relative;
}

.filter-icon {
  font-size: 18px;
}

.filter-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background-color: #ff4400;
  border-radius: 50%;
}

/* 高级筛选弹窗样式 */
.filter-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
}

.filter-modal {
  background-color: white;
  width: 100%;
  max-height: 80vh;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
}

.filter-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.filter-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.filter-modal-header .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.filter-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.filter-section {
  margin-bottom: 24px;
}

.filter-section h4 {
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #333;
  font-weight: 600;
}

.price-range-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.price-range-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background-color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.price-range-btn:hover {
  border-color: #ff4400;
  color: #ff4400;
}

.price-range-btn.active {
  background-color: #ff4400;
  color: white;
  border-color: #ff4400;
}

.filter-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
}

.filter-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.filter-modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.filter-modal-footer .btn {
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.filter-modal-footer .btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

.filter-modal-footer .btn-secondary:hover {
  background-color: #e8e8e8;
}

.filter-modal-footer .btn-primary {
  background-color: #ff4400;
  color: white;
}

.filter-modal-footer .btn-primary:hover {
  background-color: #e63d00;
}

.address-list {
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 15px;
}

.address-option {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.address-option:hover {
  border-color: #007bff;
}

.address-option.selected {
  border-color: #007bff;
  background-color: #e7f3ff;
}

.selector-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.success-modal {
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  text-align: center;
}

.success-modal h3 {
  margin: 0 0 10px 0;
  color: #28a745;
}

.success-modal p {
  margin: 0 0 20px 0;
  color: #666;
}

/* 统一非模态Toast提示容器 */
.toast-container {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toast {
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: toastSlideIn 0.3s ease;
  min-width: 200px;
}

@keyframes toastSlideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.toast-icon {
  font-size: 16px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.toast-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast-success .toast-icon {
  background-color: #28a745;
  color: white;
}

.toast-error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.toast-error .toast-icon {
  background-color: #dc3545;
  color: white;
}

.toast-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.toast-warning .toast-icon {
  background-color: #ffc107;
  color: #856404;
}

.toast-info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.toast-info .toast-icon {
  background-color: #17a2b8;
  color: white;
}

.toast-message {
  flex: 1;
}

.toast-close {
  width: 24px;
  height: 24px;
  border: none;
  background-color: transparent;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  opacity: 1;
}

.alert {
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.alert-info {
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.card {
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: white;
}

/* 尺码选择弹窗样式 */
.size-selector-modal {
  max-width: 500px;
  margin: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.size-selector-modal .modal-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.size-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  padding: 15px 20px;
  margin-bottom: 15px;
}

.size-option {
  padding: 12px 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background-color: white;
}

.size-option:hover:not(.out-of-stock) {
  border-color: #ff6b6b;
  background-color: #fff5f5;
}

.size-option.selected {
  border-color: #ff6b6b;
  background-color: #fff5f5;
  box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.2);
}

.size-option.out-of-stock {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.size-code {
  font-weight: bold;
  font-size: 16px;
  color: #333;
}

.size-name {
  font-size: 14px;
  color: #666;
}

.size-stock {
  font-size: 12px;
  color: #999;
}

.size-extra-price {
  font-size: 12px;
  color: #ff6b6b;
  font-weight: bold;
}

.selected-size-info {
  padding: 12px 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 15px;
}

.selected-size-info p {
  margin: 5px 0;
  font-size: 14px;
  color: #333;
}

.quantity-selector {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.quantity-selector label {
  font-weight: 500;
  color: #333;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quantity-controls input[type="number"] {
  width: 60px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

.quantity-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.quantity-btn:hover:not(:disabled) {
  background-color: #ff6b6b;
  color: white;
  border-color: #ff6b6b;
}

.quantity-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.no-size-info {
  text-align: center;
  padding: 30px 20px;
  color: #666;
  font-size: 14px;
}

/* 统一CheckoutForm样式 */
.unified-checkout {
  max-height: 85vh;
}

.unified-checkout .checkout-form {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.home {
  position: relative;
  overflow: hidden;
  color: var(--color-text);
}

.home::before,
.home::after {
  content: '';
  position: fixed;
  z-index: -1;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(30px);
  opacity: 0.8;
}

.home::before {
  top: 80px;
  right: -120px;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(0, 113, 227, 0.16) 0%, rgba(0, 113, 227, 0.04) 42%, transparent 72%);
  animation: ambientFloat 18s ease-in-out infinite;
}

.home::after {
  left: -180px;
  bottom: 8vh;
  width: 520px;
  height: 520px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.92) 0%, rgba(215, 232, 255, 0.38) 40%, transparent 72%);
  animation: ambientFloatReverse 24s ease-in-out infinite;
}

.category-nav-section {
  position: relative;
  background: transparent;
  border-bottom: none;
}

.category-nav-section::before {
  display: none;
}

.category-nav-wrapper {
  position: relative;
  overflow: hidden;
}

.category-nav-wrapper::after {
  display: none;
}

.products-container {
  position: relative;
  margin-bottom: 1.5rem;
  padding: 18px 12px 24px;
  background: transparent;
}

.products-container::before {
  display: none;
}

.product-list {
  position: relative;
}

.product-list.list-view .product-card {
  padding-bottom: 1rem;
}

.product-card {
  padding-bottom: 4.75rem;
  animation: cardEntrance 520ms var(--ease-standard) both;
  transform-origin: center bottom;
}

.product-card:nth-child(2n) {
  animation-delay: 40ms;
}

.product-card:nth-child(3n) {
  animation-delay: 90ms;
}

.product-card:nth-child(4n) {
  animation-delay: 130ms;
}

.product-card.highlighted {
  border: 1px solid rgba(0, 113, 227, 0.28);
  box-shadow: 0 0 0 1px rgba(0, 113, 227, 0.1), 0 18px 40px rgba(0, 113, 227, 0.14);
}

.product-image {
  overflow: hidden;
}

.product-info {
  position: relative;
  z-index: 1;
}

.product-name {
  transition: transform var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard);
}

.product-card:hover .product-name {
  transform: translateY(-1px);
  color: #111113;
}

.product-card:hover .product-img {
  transform: scale(1.035);
}

.product-actions {
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 2;
  display: flex;
  gap: 0.7rem;
  padding: 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  opacity: 0;
  pointer-events: none;
  transform: translateY(calc(100% + 1rem));
  transition:
    opacity var(--duration-base) var(--ease-standard),
    transform var(--duration-base) var(--ease-standard);
}

.product-actions.show {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.action-btn {
  min-height: 44px;
  transition:
    transform var(--duration-fast) var(--ease-standard),
    background-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
}

.buy-btn,
.cart-btn {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.buy-btn:hover:not(:disabled),
.cart-btn:hover:not(:disabled) {
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.category-item,
.sub-category-item {
  position: relative;
  overflow: hidden;
}

.icon-btn:hover,
.view-btn:hover {
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.1);
  border-color: rgba(0, 113, 227, 0.22);
  transform: scale(1.04);
}

.product-card:hover {
  transform: scale(1.015) translateY(-4px);
  border-color: rgba(0, 113, 227, 0.2);
}

.category-item:hover,
.sub-category-item:hover {
  transform: scale(1.04);
  border-color: rgba(0, 113, 227, 0.18);
}

.search-modal,
.sort-modal,
.filter-modal {
  overflow: hidden;
}

.suggestion-item,
.hot-search-item {
  transition:
    transform var(--duration-fast) var(--ease-standard),
    background-color var(--duration-fast) var(--ease-standard);
}

.suggestion-item:hover,
.hot-search-item:hover {
  transform: translateX(4px);
}

.search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.16);
}

.pagination {
  padding-bottom: 0;
}

.page-info {
  color: var(--color-text);
}

.category-nav-wrapper,
.sub-category-nav,
.search-modal,
.sort-modal,
.filter-modal,
.size-selector-modal,
.checkout-form-container,
.address-selector,
.success-modal {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: var(--shadow-soft);
}

.category-nav-wrapper {
  border-radius: 28px;
  padding: 1rem 1.2rem;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.08);
}

.sub-category-nav {
  margin-top: 0.9rem;
  border-radius: 24px;
}

.products-container {
  padding: 18px 0 24px;
}

.category-item,
.sub-category-item,
.view-btn,
.icon-btn,
.sort-option,
.price-range-btn {
  border-radius: 999px;
  border: 1px solid transparent;
  background: rgba(15, 23, 42, 0.04);
  color: var(--color-text-soft);
  transition:
    background-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);
}

.category-item:hover,
.sub-category-item:hover,
.view-btn:hover,
.icon-btn:hover,
.sort-option:hover,
.price-range-btn:hover {
  transform: translateY(-1px);
  background: rgba(15, 23, 42, 0.07);
  color: var(--color-text);
}

.category-item.active,
.sub-category-item.active,
.view-btn.active,
.sort-option.active,
.price-range-btn.active {
  background: rgba(0, 113, 227, 0.08);
  border-color: rgba(0, 113, 227, 0.16);
  color: var(--color-accent-strong);
}

.icon-btn,
.view-btn {
  width: 42px;
  height: 42px;
}

.filter-badge {
  background: var(--color-accent);
  box-shadow: 0 6px 12px rgba(0, 113, 227, 0.22);
}

.search-modal,
.sort-modal,
.filter-modal {
  border-radius: 28px;
}

.search-input {
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: rgba(255, 255, 255, 0.96);
}

.search-input:focus {
  border-color: rgba(0, 113, 227, 0.45);
  box-shadow: var(--shadow-focus);
}

.search-btn {
  border-radius: 999px;
  background: #1d1d1f;
}

.product-card {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
  overflow: hidden;
}

.product-card:hover {
  box-shadow: var(--shadow-medium);
  transform: translateY(-6px);
}

.product-image {
  background: linear-gradient(180deg, #fbfbfd, #f1f2f5);
  border-radius: 22px 22px 0 0;
}

.product-img {
  object-fit: cover;
}

.product-info {
  padding: 1rem 1.1rem 1.2rem;
}

.product-name {
  color: var(--color-text);
  font-weight: 600;
  line-height: 1.45;
}

.product-price {
  color: var(--color-text);
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.product-actions {
  padding: 0.65rem;
}

.action-btn {
  border-radius: 999px;
  font-weight: 600;
}

.buy-btn {
  background: #1d1d1f;
  color: #fff;
}

.cart-btn {
  background: rgba(15, 23, 42, 0.06);
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.buy-btn:hover,
.cart-btn:hover {
  transform: translateY(-1px);
}

.pagination .btn {
  min-width: 110px;
}

.page-info {
  color: var(--color-text-soft);
}

.hero-section {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  gap: 2rem;
  align-items: center;
  min-height: 500px;
  margin: 0 0 1.8rem;
  padding: 2rem 2rem 2.4rem;
  border-radius: 40px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.86), rgba(255, 255, 255, 0.36)),
    radial-gradient(circle at 20% 20%, rgba(0, 113, 227, 0.16), transparent 28%),
    radial-gradient(circle at 80% 26%, rgba(255, 255, 255, 0.95), rgba(220, 236, 255, 0.44) 36%, transparent 62%),
    linear-gradient(180deg, rgba(248, 249, 251, 0.94), rgba(236, 239, 245, 0.78));
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: var(--shadow-medium);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

.hero-section::before,
.hero-section::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.hero-section::before {
  width: 420px;
  height: 420px;
  right: -120px;
  top: -80px;
  background: radial-gradient(circle, rgba(0, 113, 227, 0.18), transparent 65%);
  animation: ambientFloat 16s ease-in-out infinite;
}

.hero-section::after {
  width: 360px;
  height: 360px;
  left: -120px;
  bottom: -160px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.92), rgba(226, 236, 255, 0.48) 45%, transparent 70%);
  animation: ambientFloatReverse 20s ease-in-out infinite;
}

.hero-content,
.hero-visual {
  position: relative;
  z-index: 1;
}

.hero-eyebrow {
  margin: 0 0 0.8rem;
  color: var(--color-text-faint);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-title {
  margin: 0;
  max-width: 9ch;
  font-size: clamp(2.8rem, 4vw, 4.5rem);
  line-height: 0.98;
  letter-spacing: -0.06em;
  color: var(--color-text);
}

.hero-description {
  max-width: 560px;
  margin: 1.15rem 0 0;
  color: var(--color-text-soft);
  font-size: 1.04rem;
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  gap: 0.9rem;
  margin-top: 1.6rem;
}

.hero-primary {
  min-width: 128px;
}

.hero-secondary {
  min-width: 128px;
}

.hero-stats {
  display: flex;
  gap: 0.9rem;
  margin-top: 1.8rem;
  flex-wrap: wrap;
}

.hero-stat {
  min-width: 120px;
  padding: 0.95rem 1rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.06);
}

.hero-stat-value {
  display: block;
  font-size: 1.55rem;
  font-weight: 700;
  letter-spacing: -0.04em;
  color: var(--color-text);
}

.hero-stat-label {
  display: block;
  margin-top: 0.3rem;
  color: var(--color-text-soft);
  font-size: 0.84rem;
}

.hero-visual {
  min-height: 360px;
}

.hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(8px);
}

.hero-orb-one {
  width: 220px;
  height: 220px;
  top: 10px;
  right: 24px;
  background: radial-gradient(circle, rgba(0, 113, 227, 0.24), transparent 66%);
  animation: heroOrbOne 14s ease-in-out infinite;
}

.hero-orb-two {
  width: 180px;
  height: 180px;
  left: 10px;
  bottom: 24px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.95), rgba(217, 232, 255, 0.52) 46%, transparent 72%);
  animation: heroOrbTwo 18s ease-in-out infinite;
}

.hero-orb-three {
  width: 120px;
  height: 120px;
  right: 90px;
  bottom: 36px;
  background: radial-gradient(circle, rgba(15, 23, 42, 0.11), transparent 70%);
  animation: heroOrbThree 11s ease-in-out infinite;
}

.hero-panel {
  position: absolute;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.76);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.hero-panel strong {
  display: block;
  color: var(--color-text);
  font-size: 1.35rem;
  letter-spacing: -0.03em;
}

.hero-panel-label {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--color-text-faint);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-panel-front {
  top: 42px;
  right: 24px;
  width: 220px;
  padding: 1.2rem 1.25rem;
  animation: heroPanelFront 12s ease-in-out infinite;
}

.hero-panel-back {
  left: 30px;
  bottom: 30px;
  width: 210px;
  padding: 1rem 1.15rem;
  animation: heroPanelBack 15s ease-in-out infinite;
}

.action-icons {
  position: relative;
  z-index: 3;
}

.category-nav-section::before,
.category-nav-wrapper::after {
  pointer-events: none;
}

.icon-btn,
.view-btn {
  position: relative;
  z-index: 1;
  display: inline-grid;
  place-items: center;
  overflow: hidden;
}

.search-modal-overlay,
.sort-modal-overlay,
.filter-modal-overlay {
  z-index: 1400;
}

.search-modal-overlay,
.sort-modal-overlay {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 116px 16px 24px;
}

.search-modal,
.sort-modal {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  width: min(960px, 100%);
  margin: 0 auto;
  border-radius: 30px;
}

.search-modal {
  padding: 1.2rem 1.35rem 1.35rem;
}

.search-form {
  position: relative;
  z-index: 2;
}

.search-input {
  min-height: 54px;
}

.search-btn {
  min-width: 110px;
  min-height: 54px;
}

.sort-modal {
  padding: 1.15rem 1.25rem 1.2rem;
}

.sort-options {
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 0.8rem;
}

@keyframes ambientFloat {
  0%, 100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(-36px, 28px, 0) scale(1.08);
  }
}

@keyframes ambientFloatReverse {
  0%, 100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(40px, -30px, 0) scale(0.94);
  }
}

@keyframes glowDrift {
  0% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(-60px, -24px, 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}

@keyframes cardEntrance {
  from {
    opacity: 0;
    transform: translateY(26px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes heroOrbOne {
  0%, 100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(-30px, 18px, 0) scale(1.08);
  }
}

@keyframes heroOrbTwo {
  0%, 100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(24px, -20px, 0) scale(0.95);
  }
}

@keyframes heroOrbThree {
  0%, 100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(-18px, -12px, 0);
  }
}

@keyframes heroPanelFront {
  0%, 100% {
    transform: translate3d(0, 0, 0) rotate(0deg);
  }
  50% {
    transform: translate3d(-12px, 14px, 0) rotate(-2deg);
  }
}

@keyframes heroPanelBack {
  0%, 100% {
    transform: translate3d(0, 0, 0) rotate(0deg);
  }
  50% {
    transform: translate3d(14px, -10px, 0) rotate(2deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .home::before,
  .home::after,
  .category-nav-wrapper::after,
  .product-card,
  .hero-section::before,
  .hero-section::after,
  .hero-orb,
  .hero-panel {
    animation: none;
  }
}

@media (max-width: 768px) {
  .hero-section {
    grid-template-columns: 1fr;
    min-height: auto;
    padding: 1.5rem 1.2rem 1.7rem;
    border-radius: 28px;
  }

  .hero-title {
    max-width: none;
    font-size: 2.6rem;
  }

  .hero-actions,
  .hero-stats {
    flex-direction: column;
  }

  .hero-stat {
    width: 100%;
  }

  .hero-visual {
    min-height: 260px;
  }

  .hero-panel-front {
    right: 12px;
    width: 180px;
  }

  .hero-panel-back {
    left: 12px;
    width: 170px;
  }

  .home::before {
    width: 280px;
    height: 280px;
    right: -90px;
  }

  .home::after {
    width: 320px;
    height: 320px;
    left: -120px;
  }

  .category-nav-section::before {
    inset: -8px 0 auto;
    height: 240px;
    border-radius: 28px;
  }

  .products-container {
    padding-left: 0;
    padding-right: 0;
  }

  .products-container::before {
    border-radius: 28px;
  }

  .product-card {
    padding-bottom: 5rem;
  }

  .product-actions {
    left: 0.8rem;
    right: 0.8rem;
    bottom: 0.8rem;
    gap: 0.55rem;
  }

  .search-modal-overlay,
  .sort-modal-overlay {
    padding-top: 102px;
  }
}
</style>
